import * as React from "react";
import { Card, Table, Tag, Space, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import Swal from "sweetalert2";
import { snackbarStore } from "src/store/snackbarStore";
import CustomButton from "src/components/common/CustomButton";
import WarehouseFilterBar from "src/components/warehouse/WarehouseFilterBar";
import WarehouseUpsertModal from "src/components/warehouse/WarehouseUpsertModal";
import {
  getWarehouseListApi,
  deleteWarehouseApi,
  type WarehouseListItem,
} from "src/api/warehouse/warehouseApi";
import type { InventoryType } from "src/types/warehouse";

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

export default function WarehouseManagePage() {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<WarehouseListItem[]>([]);
  const [total, setTotal] = React.useState(0);

  // Filter states
  const [warehouseName, setWarehouseName] = React.useState("");
  const [inventoryType, setInventoryType] = React.useState<InventoryType | "">("");
  const [isActive, setIsActive] = React.useState<boolean | "">("");
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(20);

  // Modal states
  const [upsertModalOpen, setUpsertModalOpen] = React.useState(false);
  const [editingItem, setEditingItem] = React.useState<WarehouseListItem | null>(null);

  const fetchList = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await getWarehouseListApi({
        warehouseName: warehouseName || undefined,
        inventoryType: inventoryType || undefined,
        isActive: isActive === "" ? undefined : isActive,
        page,
        size,
        order: "createdAt,desc",
      });

      if (res.statusCode === 1 && res.data) {
        setData(res.data.content);
        setTotal(res.data.page.totalElements);
      } else {
        toast(res.message || "목록 조회 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "목록을 불러오는 중 오류가 발생했습니다.", "error");
    } finally {
      setLoading(false);
    }
  }, [warehouseName, inventoryType, isActive, page, size]);

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSearch = () => {
    setPage(0);
    fetchList();
  };

  const handleCreate = () => {
    setEditingItem(null);
    setUpsertModalOpen(true);
  };

  const handleEdit = (item: WarehouseListItem) => {
    setEditingItem(item);
    setUpsertModalOpen(true);
  };

  const handleDelete = async (item: WarehouseListItem) => {
    const result = await Swal.fire({
      title: "창고 삭제",
      text: `"${item.warehouseName}" 창고를 삭제하시겠습니까?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "삭제",
      cancelButtonText: "취소",
      confirmButtonColor: "#d33",
    });

    if (result.isConfirmed) {
      try {
        const res = await deleteWarehouseApi(item.warehouseId);
        if (res.statusCode === 1) {
          toast("창고가 삭제되었습니다.", "success");
          fetchList();
        } else {
          toast(res.message || "창고 삭제 실패", "error");
        }
      } catch (e: any) {
        toast(e?.message || "창고 삭제 중 오류가 발생했습니다.", "error");
      }
    }
  };

  const handleModalClose = () => {
    setUpsertModalOpen(false);
    setEditingItem(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchList();
  };

  const columns: ColumnsType<WarehouseListItem> = [
    {
      title: "창고코드",
      dataIndex: "warehouseCode",
      align: "center",
      key: "warehouseCode",
      width: 150,
    },
    {
      title: "창고명",
      dataIndex: "warehouseName",
      align: "center",
      key: "warehouseName",
      width: 200,
    },
    {
      title: "재고관리타입",
      dataIndex: "inventoryType",
      align: "center",
      key: "inventoryType",
      width: 150,
      render: (type: InventoryType) => {
        const colorMap = {
          MANUAL: "default",
          WMS: "blue",
          DELEGATE: "green",
        };
        return <Tag color={colorMap[type]}>{type}</Tag>;
      },
    },
    {
      title: "외부공급자",
      dataIndex: "externalProvider",
      align: "center",
      key: "externalProvider",
      width: 150,
      render: (text) => text || "-",
    },
    {
      title: "외부창고ID",
      dataIndex: "externalWarehouseId",
      align: "center",
      key: "externalWarehouseId",
      width: 150,
      render: (text) => text || "-",
    },
    {
      title: "위탁사",
      dataIndex: "delegateName",
      align: "center",
      key: "delegateName",
      width: 150,
      render: (text) => text || "-",
    },
    {
      title: "활성상태",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      align: "center",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "red"}>{isActive ? "활성" : "비활성"}</Tag>
      ),
    },
    {
      title: "생성일",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (text) => new Date(text).toLocaleString("ko-KR"),
    },
    {
      title: "수정일",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      render: (text) => new Date(text).toLocaleString("ko-KR"),
    },
    {
      title: "관리",
      key: "action",
      width: 150,
      fixed: "right",
      render: (_, record) => (
        <Space size={8}>
          <CustomButton variant="outlined" size="small" onClick={() => handleEdit(record)}>
            수정
          </CustomButton>
          <CustomButton variant="outlined" size="small" danger onClick={() => handleDelete(record)}>
            삭제
          </CustomButton>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
          창고 관리
        </Typography.Title>

        <WarehouseFilterBar
          warehouseName={warehouseName}
          inventoryType={inventoryType}
          isActive={isActive}
          onChangeWarehouseName={setWarehouseName}
          onChangeInventoryType={setInventoryType}
          onChangeIsActive={setIsActive}
          onSearch={handleSearch}
          onCreate={handleCreate}
        />

        <Table
          columns={columns}
          dataSource={data}
          rowKey={(record) => record.warehouseId}
          loading={loading}
          pagination={{
            current: page + 1,
            pageSize: size,
            total,
            onChange: (p, s) => {
              setPage(p - 1);
              setSize(s);
            },
            showSizeChanger: true,
            showTotal: (total) => `총 ${total}개`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <WarehouseUpsertModal
        open={upsertModalOpen}
        editingItem={editingItem}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
}
